import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { atsFetch, AtsClient, AtsRole, fmtMoney, timeAgo } from './api';
import { Modal, Field, inputCls, btnPrimary, btnGhost, StatusBadge, ClientPicker } from './shared';

const AtsRoles: React.FC = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [roles, setRoles] = useState<AtsRole[]>([]);
  const [clients, setClients] = useState<AtsClient[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ clientId: '', title: '', location: '', employmentType: 'permanent', feeType: 'percent', feeValue: '', salaryMin: '', salaryMax: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showClosed, setShowClosed] = useState(false);

  const load = useCallback(() => {
    if (!token) return;
    setLoading(true);
    Promise.all([atsFetch(token, '/roles'), atsFetch(token, '/clients')])
      .then(([r, c]) => { setRoles(r.roles); setClients(c.clients); })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(load, [load]);

  const create = async () => {
    if (!token || !form.clientId || !form.title.trim()) return;
    setSaving(true);
    try {
      const d = await atsFetch(token, '/roles', {
        method: 'POST',
        body: JSON.stringify({
          clientId: Number(form.clientId), title: form.title, location: form.location || null,
          employmentType: form.employmentType, feeType: form.feeType,
          feeValue: form.feeValue === '' ? null : Number(form.feeValue),
          salaryMin: form.salaryMin === '' ? null : Number(form.salaryMin),
          salaryMax: form.salaryMax === '' ? null : Number(form.salaryMax),
        }),
      });
      navigate(`/ats/roles/${d.role.id}`);
    } catch (e: any) { setError(e.message); }
    setSaving(false);
  };

  const visible = roles.filter((r) => showClosed || ['lead', 'open', 'interviewing'].includes(r.status));

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-4">
        <label className="flex items-center gap-2 text-xs text-gray-500">
          <input type="checkbox" checked={showClosed} onChange={(e) => setShowClosed(e.target.checked)} className="rounded border-gray-300" />
          Show filled / lost
        </label>
        <button onClick={() => setShowCreate(true)} className={btnPrimary} disabled={clients.length === 0} title={clients.length === 0 ? 'Add a client first' : undefined}>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
          New role
        </button>
      </div>
      {error && <p className="text-sm text-red-600 mb-3">{error}</p>}

      {loading ? (
        <div className="py-16 text-center text-gray-400 text-sm">Loading…</div>
      ) : visible.length === 0 ? (
        <div className="py-16 text-center">
          <p className="text-sm text-gray-500 mb-1">No open roles.</p>
          <p className="text-xs text-gray-400">{clients.length === 0 ? 'Add a client first, then open a role under it.' : 'Open a role to start running a pipeline.'}</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-gray-400 border-b border-gray-100">
                <th className="px-5 py-2.5 font-medium">Role</th>
                <th className="px-3 py-2.5 font-medium">Client</th>
                <th className="px-3 py-2.5 font-medium">Status</th>
                <th className="px-3 py-2.5 font-medium text-center">Pipeline</th>
                <th className="px-3 py-2.5 font-medium text-center">Placed</th>
                <th className="px-3 py-2.5 font-medium">Salary</th>
                <th className="px-5 py-2.5 font-medium text-right">Updated</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((r) => (
                <tr key={r.id} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/60">
                  <td className="px-5 py-3"><Link to={`/ats/roles/${r.id}`} className="font-medium text-gray-900 hover:text-blue-700">{r.title}</Link></td>
                  <td className="px-3 py-3 text-gray-600">{r.client_name}</td>
                  <td className="px-3 py-3"><StatusBadge value={r.status} /></td>
                  <td className="px-3 py-3 text-center text-gray-700">{r.active_cards || 0}</td>
                  <td className="px-3 py-3 text-center text-gray-700">{r.placed_cards || 0}</td>
                  <td className="px-3 py-3 text-gray-500 text-xs">
                    {r.salary_min || r.salary_max
                      ? `${fmtMoney(r.salary_min, r.currency)} – ${fmtMoney(r.salary_max, r.currency)}`
                      : '—'}
                  </td>
                  <td className="px-5 py-3 text-right text-xs text-gray-400">{timeAgo(r.updated_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && (
        <Modal title="New role" onClose={() => setShowCreate(false)}>
          <div className="space-y-3">
            <Field label="Client *">
              <ClientPicker
                clients={clients}
                valueId={form.clientId ? Number(form.clientId) : null}
                onSelect={(id) => setForm({ ...form, clientId: String(id) })}
                onCreate={async (name) => {
                  if (!token) return null;
                  const d = await atsFetch(token, '/clients', { method: 'POST', body: JSON.stringify({ name }) });
                  setClients((prev) => [d.client, ...prev]);
                  return d.client;
                }}
                autoFocus
              />
            </Field>
            <Field label="Title *"><input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Location"><input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className={inputCls} /></Field>
              <Field label="Type">
                <select value={form.employmentType} onChange={(e) => setForm({ ...form, employmentType: e.target.value })} className={inputCls}>
                  <option value="permanent">Permanent</option>
                  <option value="contract">Contract</option>
                  <option value="temp">Temp</option>
                  <option value="fixed_term">Fixed term</option>
                </select>
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Salary min"><input type="number" value={form.salaryMin} onChange={(e) => setForm({ ...form, salaryMin: e.target.value })} className={inputCls} /></Field>
              <Field label="Salary max"><input type="number" value={form.salaryMax} onChange={(e) => setForm({ ...form, salaryMax: e.target.value })} className={inputCls} /></Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Fee type">
                <select value={form.feeType} onChange={(e) => setForm({ ...form, feeType: e.target.value })} className={inputCls}>
                  <option value="percent">% of salary</option>
                  <option value="fixed">Fixed fee</option>
                </select>
              </Field>
              <Field label={form.feeType === 'percent' ? 'Fee %' : 'Fee amount'}>
                <input type="number" value={form.feeValue} onChange={(e) => setForm({ ...form, feeValue: e.target.value })} className={inputCls} />
              </Field>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={() => setShowCreate(false)} className={btnGhost}>Cancel</button>
              <button onClick={create} disabled={saving || !form.clientId || !form.title.trim()} className={btnPrimary}>{saving ? 'Creating…' : 'Create role'}</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default AtsRoles;
